/**
 * Offline-first IndexedDB store via Dexie. Holds queries, dossiers,
 * agent conversations, and a sync queue that drains when network returns.
 */
import Dexie, { type Table } from "dexie";

export interface CachedDossier {
  id: string;
  user_id: string;
  query: string;
  payload: unknown;
  updated_at: number;
}

export interface QueuedMutation {
  id?: number;
  kind: string;            // e.g. "research:create", "agent:chat"
  payload: unknown;
  created_at: number;
  attempts: number;
}

class NeuralDB extends Dexie {
  dossiers!: Table<CachedDossier, string>;
  queue!: Table<QueuedMutation, number>;

  constructor() {
    super("neural-core-os");
    this.version(1).stores({
      dossiers: "id, user_id, updated_at",
      queue: "++id, kind, created_at",
    });
  }
}

export const db = new NeuralDB();

export async function enqueueMutation(kind: string, payload: unknown) {
  return db.queue.add({ kind, payload, created_at: Date.now(), attempts: 0 });
}

export async function drainQueue(handler: (m: QueuedMutation) => Promise<void>) {
  const items = await db.queue.orderBy("created_at").toArray();
  for (const item of items) {
    try {
      await handler(item);
      if (item.id !== undefined) await db.queue.delete(item.id);
    } catch (e) {
      if (item.id !== undefined) {
        await db.queue.update(item.id, { attempts: item.attempts + 1 });
      }
      console.warn("[queue] item failed", item.kind, e);
    }
  }
}
